import { ButtonSize, ButtonRole, ButtonTargetKind } from "@components/Button";
import { CoverImageAltTextPopover } from "@components/CoverImageAltTextPopover";
import { ErrorMessage } from "@components/ErrorMessage";
import { Icon, IconSize, IconDisplay } from "@components/Icon";
import { ImageFileInput } from "@components/ImageFileInput";
import { Loading } from "@components/LoadingIndicator";
import { useUploadImage } from "@components/Nonprofit/EditForm/PicturesUploading";
import { useDragging } from "@components/Tags/ImageUploadingSection";
import { TextInput } from "@components/TextInput";
import { useRef, useState, useMemo } from "react";

import { constructCloudinaryUrl } from "@every.org/common/src/helpers/cloudinary";

import {
  ImageContainer,
  LoadingOverlay,
  Image,
  FullWidthButton,
  PlusButton,
  plusIconContentCss,
  PlusIconContainer,
  EditButton,
  StyledDropZone,
  DropZoneTitle,
} from "src/components/CoverPhotoOrVideoPicker/styles";
import { CoverPhotoOrVideoPickerProps } from "src/components/CoverPhotoOrVideoPicker/types";
import { verticalStackCss } from "src/theme/spacing";

const constructUrl = (cloudinaryId: string | null) =>
  cloudinaryId &&
  constructCloudinaryUrl({
    cloudinaryId,
    dimensions: { height: 380 },
  });

export function PhotoCoverPreview(props: CoverPhotoOrVideoPickerProps) {
  const uploadCoverImageRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string>();
  const { dragging, setDragging } = useDragging();

  const { uploadImage, loading } = useUploadImage((id) => {
    props.setCoverImageId(id);
  }, setError);

  const customCoverImageId = useMemo(
    () =>
      props.coverImageId === props.defaultCoverImageId
        ? null
        : props.coverImageId,
    [props.coverImageId, props.defaultCoverImageId]
  );

  const customCoverImageUrl = useMemo(
    () => customCoverImageId && constructUrl(customCoverImageId),
    [customCoverImageId]
  );

  return (
    <div css={verticalStackCss.m}>
      <ImageContainer>
        <Image url={customCoverImageUrl}>
          {loading ? (
            <LoadingOverlay>
              <Loading height={"24px"} />
            </LoadingOverlay>
          ) : customCoverImageId ? (
            <FullWidthButton
              data-tname={"SelectUploadedImage_Button"}
              size={ButtonSize.SMALL}
              role={ButtonRole.UNSTYLED}
              onClick={{
                kind: ButtonTargetKind.FUNCTION,
                action: () => {
                  props.setCoverImageId(customCoverImageId);
                },
              }}
            />
          ) : (
            <PlusButton
              aria-label="Add cover image"
              data-tname={"AddCoverImage_Button"}
              size={ButtonSize.SMALL}
              role={ButtonRole.UNSTYLED}
              contentCss={plusIconContentCss}
              onClick={{
                kind: ButtonTargetKind.FUNCTION,
                action: () => {
                  const input = uploadCoverImageRef.current;
                  if (input) {
                    input.click();
                  }
                },
              }}
            >
              <PlusIconContainer>
                <Icon
                  iconImport={() =>
                    import("src/components/Icon/icons/PlusIcon")
                  }
                  size={IconSize.MEDIUM}
                  display={IconDisplay.ACCENT}
                />
              </PlusIconContainer>
              <span>
                {error ? (
                  <ErrorMessage>{error}</ErrorMessage>
                ) : (
                  <span>Choose a cover photo</span>
                )}
              </span>
            </PlusButton>
          )}
          {customCoverImageId && (
            <EditButton
              aria-label="Edit cover image"
              data-tname={"EditCoverImage_Button"}
              size={ButtonSize.SMALL}
              role={ButtonRole.TEXT_ONLY}
              disabled={loading}
              icon={
                <Icon
                  iconImport={() =>
                    import("src/components/Icon/icons/EditIcon")
                  }
                  size={IconSize.MEDIUM}
                  display={IconDisplay.CURRENT_COLOR}
                />
              }
              onClick={{
                kind: ButtonTargetKind.FUNCTION,
                action: () => {
                  const input = uploadCoverImageRef.current;
                  if (input) {
                    input.click();
                  }
                },
              }}
            />
          )}
          <ImageFileInput
            ref={uploadCoverImageRef}
            onChange={({ currentTarget }) => uploadImage(currentTarget)}
          />
          <StyledDropZone
            dragging={dragging}
            onDropCapture={({ dataTransfer }) => {
              setDragging(false);
              uploadImage(dataTransfer);
            }}
          >
            <DropZoneTitle>Drop picture here</DropZoneTitle>
          </StyledDropZone>
        </Image>
      </ImageContainer>
      <TextInput
        name="coverImageAltText"
        data-tname="coverImageAltText"
        value={props.coverImageAltText}
        onChange={(e) => {
          props.setCoverImageAltText(e.target.value);
        }}
        placeholder="Insert alt text for cover photo"
        inputSuffix={<CoverImageAltTextPopover />}
        css={{ maxWidth: "unset" }}
        collapseDescriptionSpace
      />
    </div>
  );
}

export function DefaultCoverPreview(props: CoverPhotoOrVideoPickerProps) {
  const defaultCoverImageUrl = useMemo(
    () => constructUrl(props.defaultCoverImageId),
    [props.defaultCoverImageId]
  );

  return (
    <ImageContainer>
      <Image url={defaultCoverImageUrl} />
    </ImageContainer>
  );
}
